import { useEffect, useMemo, useRef, useState } from 'react';

import {
  type ClientMessageData,
  useGetDialogHistoryDialogHistoryGetQuery,
  useLazyGetDialogHistoryDialogHistoryGetQuery,
} from '@shared/api';

interface UseGetMessagesProps {
  clientId: number;
  skip?: boolean;
  pageSize?: number;
}

export interface UseGetMessagesResult {
  messages: ClientMessageData[];
  count: number;
  currentPage: number;
  totalPages: number;
  hasMore: boolean;
  isEmpty: boolean;
  isError: boolean;
  error: unknown;
  isRefetching: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isUninitialized: boolean;
  isFetchingMore: boolean;
  isLoadingMore: boolean;
  fetchNextData: () => Promise<void>;
  handleRefetch: () => Promise<void>;
}

export const useGetMessages = ({
  clientId,
  skip = false,
  pageSize = 20,
}: UseGetMessagesProps): UseGetMessagesResult => {
  const [baseMessages, setBaseMessages] = useState<ClientMessageData[]>([]);
  const [olderMessages, setOlderMessages] = useState<ClientMessageData[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [isRefetching, setIsRefetching] = useState(false);
  const fetchedIdsRef = useRef<Set<number>>(new Set());
  const requestedOffsetsRef = useRef<Set<number>>(new Set());
  const [fetchedCount, setFetchedCount] = useState(0);
  const oldestOffsetRef = useRef(0);

  const {
    data: latestPage,
    isFetching: isFetchingLatest,
    isLoading: isLoadingLatest,
    isError,
    error,
    isUninitialized,
    refetch: refetchLatest,
  } = useGetDialogHistoryDialogHistoryGetQuery(
    { clientId, offset: 0, limit: pageSize },
    {
      skip,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const [fetchOlderMessages, { isFetching: isFetchingOlder, isLoading: isLoadingOlder }] =
    useLazyGetDialogHistoryDialogHistoryGetQuery();

  const isFetching = isFetchingLatest || isFetchingOlder;
  const isLoading = isLoadingLatest || isLoadingOlder;
  const isFetchingMore = isFetchingOlder;
  const isLoadingMore = isLoadingOlder;

  useEffect(() => {
    setBaseMessages([]);
    setOlderMessages([]);
    setTotalCount(null);
    fetchedIdsRef.current = new Set();
    requestedOffsetsRef.current = new Set();
    setFetchedCount(0);
    oldestOffsetRef.current = 0;
  }, [clientId]);

  useEffect(() => {
    if (!latestPage?.items) return;

    const chunk = [...latestPage.items].reverse();
    setBaseMessages(chunk);
    const nextIds = new Set(fetchedIdsRef.current);
    chunk.forEach((msg) => nextIds.add(msg.id));
    fetchedIdsRef.current = nextIds;
    setFetchedCount(nextIds.size);
    oldestOffsetRef.current = nextIds.size;
    if (typeof latestPage.count === 'number') {
      setTotalCount(latestPage.count);
    }
  }, [latestPage?.items, latestPage?.count]);

  const messages = useMemo(() => {
    const merged = [...olderMessages, ...baseMessages];
    return merged.sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    );
  }, [olderMessages, baseMessages]);

  const hasMore =
    totalCount !== null ? fetchedCount < totalCount : (latestPage?.items?.length ?? 0) >= pageSize;

  const totalPages = totalCount ? Math.ceil(totalCount / pageSize) : 0;
  const isEmpty = !messages.length && !isFetching && !isLoading && !isUninitialized;

  const fetchNextData = async () => {
    if (!hasMore || isFetchingMore || isLoadingMore || skip) return;

    const nextOffset = oldestOffsetRef.current;
    if (requestedOffsetsRef.current.has(nextOffset)) return;
    requestedOffsetsRef.current.add(nextOffset);

    try {
      const result = await fetchOlderMessages({
        clientId,
        offset: nextOffset,
        limit: pageSize,
      }).unwrap();

      const chunk = [...(result.items ?? [])].reverse();
      const incoming = chunk.filter((msg) => !fetchedIdsRef.current.has(msg.id));

      setOlderMessages((prev) => [...incoming, ...prev]);
      incoming.forEach((msg) => fetchedIdsRef.current.add(msg.id));
      setFetchedCount(fetchedIdsRef.current.size);
      oldestOffsetRef.current = nextOffset + (result.items?.length ?? 0);

      if (typeof result.count === 'number') {
        setTotalCount(result.count);
      }
    } catch {
      requestedOffsetsRef.current.delete(nextOffset);
    }
  };

  const handleRefetch = async () => {
    setIsRefetching(true);
    await refetchLatest();
    setIsRefetching(false);
  };

  return {
    messages,
    count: totalCount ?? 0,
    currentPage: Math.floor(messages.length / pageSize),
    totalPages,
    hasMore,
    isEmpty,
    isError,
    error,
    isRefetching,
    isLoading,
    isFetching,
    isUninitialized,
    isFetchingMore,
    isLoadingMore,
    fetchNextData,
    handleRefetch,
  };
};
